import { ui } from "./styles.js";
import { lazy, Suspense, useState, type ReactNode } from "react";
import { Button, TextField } from "@snowiespace/ui/web";

const RichMarkdownEditor = lazy(() => import("./rich-markdown.js").then((module) => ({ default: module.RichMarkdownEditor })));
const RichMarkdownDocument = lazy(() => import("./rich-markdown.js").then((module) => ({ default: module.RichMarkdownDocument })));

function Inline({ value }: { value: string }) {
  const parts = value.split(/(`[^`]+`|\*\*[^*]+\*\*|\[[^\]]+\]\([^\s)]+\)|\[\[page:[a-zA-Z0-9_-]+\]\])/g);
  return <>{parts.map((part, index) => {
    if (part.startsWith("`") && part.endsWith("`")) return <code key={index}>{part.slice(1, -1)}</code>;
    if (part.startsWith("**") && part.endsWith("**")) return <strong key={index}>{part.slice(2, -2)}</strong>;
    const link = part.match(/^\[([^\]]+)\]\(([^\s)]+)\)$/);
    if (link) return <a key={index} href={link[2]} rel="noreferrer" target="_blank">{link[1]}</a>;
    if (/^\[\[page:[a-zA-Z0-9_-]+\]\]$/.test(part)) return <code className={ui("page-reference")} key={index}>{part}</code>;
    return part;
  })}</>;
}

export function MarkdownPreview({ value }: { value: string }) {
  const lines = value.replace(/\r\n/g, "\n").split("\n");
  let inCode = false;
  const rendered: ReactNode[] = [];
  let code: string[] = [];
  lines.forEach((line, index) => {
    if (line.startsWith("```")) {
      if (inCode) { rendered.push(<pre key={`code-${index}`}><code>{code.join("\n")}</code></pre>); code = []; }
      inCode = !inCode;
      return;
    }
    if (inCode) { code.push(line); return; }
    if (line.startsWith("### ")) rendered.push(<h3 key={index}><Inline value={line.slice(4)} /></h3>);
    else if (line.startsWith("## ")) rendered.push(<h2 key={index}><Inline value={line.slice(3)} /></h2>);
    else if (line.startsWith("# ")) rendered.push(<h1 key={index}><Inline value={line.slice(2)} /></h1>);
    else if (line.startsWith("- ")) rendered.push(<div className={ui("markdown-list-item")} key={index}><span aria-hidden="true">•</span><span><Inline value={line.slice(2)} /></span></div>);
    else if (line.startsWith("> ")) rendered.push(<blockquote key={index}><Inline value={line.slice(2)} /></blockquote>);
    else if (line.trim()) rendered.push(<p key={index}><Inline value={line} /></p>);
    else rendered.push(<div className={ui("markdown-break")} key={index} aria-hidden="true" />);
  });
  if (code.length) rendered.push(<pre key="code-final"><code>{code.join("\n")}</code></pre>);
  return <article className={ui("markdown-preview")} aria-label="Sanitized Markdown preview">{rendered.length ? rendered : <p className={ui("muted")}>The Markdown preview will appear here.</p>}</article>;
}

export function MarkdownEditor({ documentKey, onAttach, value, onChange }: { documentKey?: string; onAttach?: () => void; value: string; onChange: (value: string) => void }) {
  const [mode, setMode] = useState<"visual" | "source">("visual");
  return <div className={ui("markdown-editor")}>
    <div className={ui("markdown-mode-switch")} role="group" aria-label="Page editor mode">
      <Button size="compact" variant={mode === "visual" ? "secondary" : "quiet"} onPress={() => setMode("visual")}>Visual</Button>
      <Button size="compact" variant={mode === "source" ? "secondary" : "quiet"} onPress={() => setMode("source")}>Markdown</Button>
      <small>Canonical Markdown remains the saved format.</small>
    </div>
    {mode === "visual" ? <Suspense fallback={<p className={ui("markdown-loading")} role="status">Opening visual editor…</p>}><RichMarkdownEditor key={`edit:${documentKey ?? "page"}`} onAttach={onAttach} value={value} onChange={onChange} /></Suspense> : <TextField className={ui("markdown-source")} label="Canonical Markdown" value={value} onChange={onChange} textarea rows={18} />}
  </div>;
}

export function MarkdownDocument({ documentKey, value }: { documentKey?: string; value: string }) {
  return <Suspense fallback={<MarkdownPreview value={value} />}><RichMarkdownDocument key={`view:${documentKey ?? "page"}:${value}`} value={value} /></Suspense>;
}
